import { useState, useEffect } from "react";
import { ShieldCheck } from "lucide-react";
import { SubSection } from "../SubSection";
import { NavItem } from "../NavItem";
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { fetchMyRightsToConfirm } from "@/lib/track-rights-api";
import { fetchOrgPublisherMap } from "@/lib/publisher-map-api";

interface RightsSectionProps {
  isCollapsed: boolean;
  currentPath: string;
  isBuyer?: boolean;
  /** `has(CATALOG_WRITE)` — the same predicate the route's SellerGuard enforces. */
  canManageCompanies?: boolean;
}

/**
 * Rights — rights ADMIN, split out of Collaborate (2026-09-02).
 *
 * Collaborate had grown to five items doing three unrelated jobs. These two are the pair that
 * answers "who holds what, and does the platform know it": you confirm rights, and only then can
 * you trade on them — which is why this section sits BEFORE Collaborate rather than after it.
 *
 * NOT tabs on one page, deliberately. `RightsToConfirmPage` already owns a tab strip whose three
 * tabs — To confirm · Confirmed · Declined — are STATES OF ONE QUEUE. A fourth tab naming a
 * different object (your own corporate map) would mix axes: the strip would stop meaning "which
 * slice of this queue" and start meaning "which page". Different audiences, different rhythms.
 *
 * The two gates below are NOT the same and must not be collapsed into one:
 *   · Rights to Confirm → `!isBuyer`                 (seller · negotiator · admin)
 *   · Your Companies    → `has(CATALOG_WRITE)` — the SAME predicate the route's SellerGuard
 *                          enforces (seller · admin), so nav and route cannot disagree, and a
 *                          null accountType default-DENIES instead of default-allowing.
 * A buyer holds neither, so the SECTION is gated on non-buyer at its call site in
 * `Sidebar/index.tsx` — see the note there. The per-item gates stay verbatim regardless, so this
 * component is correct on its own terms and not merely correct because of where it is used.
 */
export function RightsSection({
  isCollapsed,
  currentPath,
  isBuyer,
  canManageCompanies = false,
}: RightsSectionProps) {
  // Rights awaiting THIS org's confirmation. Counted here because the whole problem was that a
  // holder had no idea anything was waiting: without a badge the page is another surface nobody
  // visits.
  //
  // BUYERS ARE EXCLUDED (2026-08-01). The earlier note here said no role check was needed because
  // the server returns an empty list for buyers. True, and not a reason: it left a permanent nav
  // item that can never hold anything, and fired a request per mount to prove it. A buyer holds no
  // rights, and a confirm invite is addressed to the org that CLAIMED the party — which OD-1
  // forbids a buyer org from doing — so this is structurally empty, not incidentally empty.
  //
  // This effect moved here WHOLE with the item it feeds; it is the section's only fetch, and no
  // other section reads it. The license-request stats fetch stayed behind in CollaborateSection
  // with the License Requests badge, its only consumer — so neither section fetches what the other
  // needs, and neither can double-fetch or race the other.
  const [toConfirm, setToConfirm] = useState(0);
  useEffect(() => {
    if (isBuyer) return;
    let cancelled = false;
    fetchMyRightsToConfirm()
      // PENDING only. The read now returns accepted/declined too (so the page can show history),
      // and counting those would show a badge for work already done.
      .then((items) => {
        if (!cancelled) {
          setToConfirm(items.filter((i) => !i.status || i.status === "pending" || i.status === "expired").length);
        }
      })
      .catch(() => { /* a nav badge must never surface an error */ });
    return () => { cancelled = true; };
  }, []);

  // Publisher World shows only once the organization has a group on the publisher map (Believe first); for
  // every other organization the page would only say "not set up yet", so the nav item stays away.
  const [hasPublisherGroup, setHasPublisherGroup] = useState(false);
  useEffect(() => {
    if (!canManageCompanies) return;
    const controller = new AbortController();
    fetchOrgPublisherMap(controller.signal)
      .then((r) => { if (!controller.signal.aborted) setHasPublisherGroup(r.linked); })
      .catch(() => { /* a nav item must never surface an error */ });
    return () => controller.abort();
  }, [canManageCompanies]);

  return (
    <SubSection title="Rights" icon={ShieldCheck} isCollapsed={isCollapsed}>
      {/* First, because it is the queue with work in it. */}
      {!isBuyer ? (
        <NavItem
          icon={VerifiedUserOutlinedIcon}
          label="Rights to Confirm"
          path="/rights-to-confirm"
          isCollapsed={isCollapsed}
          isActive={currentPath.startsWith("/rights-to-confirm")}
          badge={toConfirm > 0 ? String(toConfirm) : undefined}
        />
      ) : null}
      {/* Your Companies — the org's own slice of the corporate map, and the gap in it. Gated on
          the same predicate the route's SellerGuard enforces (seller + admin hold CATALOG_WRITE):
          a buyer org cannot claim a party at all (OD-1), and a negotiator curates no catalogue, so
          for both this is structurally empty rather than incidentally empty. No badge: the count
          that matters here is "how many of yours do NOT reach you", and a permanent amber number
          on the nav for a state that is normal on day one would be nagging, not informing. */}
      {canManageCompanies ? (
        <NavItem
          icon={AccountTreeOutlinedIcon}
          label="Your Companies"
          path="/corporate-hierarchy"
          isCollapsed={isCollapsed}
          isActive={currentPath.startsWith("/corporate-hierarchy")}
        />
      ) : null}
      {canManageCompanies && hasPublisherGroup ? (
        <NavItem
          icon={PublicOutlinedIcon}
          label="Publisher World"
          path="/publisher-world"
          isCollapsed={isCollapsed}
          isActive={currentPath.startsWith("/publisher-world")}
        />
      ) : null}
    </SubSection>
  );
}
